import { Headset } from '@phosphor-icons/react';
import type { Me } from '@sm/api';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { endImpersonation } from '@/lib/platformQueries';
import { apiErrorToast } from '@/lib/toast';

/** Preostale minute do isteka impersonacije (zaokruženo naviše, ne manje od 0). */
export function minutesLeft(expiresAt: string, now: number = Date.now()): number {
  const ms = new Date(expiresAt).getTime() - now;
  return Math.max(0, Math.ceil(ms / 60_000));
}

/** Da li je trenutna sesija impersonacija (podrška NENASAL je prijavljena kao korisnik). */
export function isImpersonating(me: Pick<Me, 'impersonation'> | undefined): boolean {
  return !!me?.impersonation;
}

/** Puna navigacija (testovi je zamjenjuju). */
export const nav = {
  assign: (url: string) => window.location.assign(url),
};

/**
 * Završava impersonaciju preko klijenta platforme i vraća operatera na detalj firme
 * (puna navigacija: keš firme se odbacuje).
 */
export async function finishImpersonation(
  me: Pick<Me, 'tenant'>,
  clear?: () => void,
): Promise<void> {
  try {
    await endImpersonation();
  } finally {
    clear?.();
    nav.assign(`/platforma/firme/${me.tenant.id}`);
  }
}

/** Trajni crveni baner na vrhu panela dok podrška NENASAL koristi nalog korisnika. */
export function ImpersonationBanner({ me }: { me: Me }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, []);
  if (!me.impersonation) return null;
  const left = minutesLeft(me.impersonation.expiresAt, now);

  const end = async () => {
    setBusy(true);
    try {
      await finishImpersonation(me, () => qc.clear());
    } catch (e) {
      setBusy(false);
      apiErrorToast(t, e);
    }
  };

  return (
    <div
      role="alert"
      data-testid="impersonation-banner"
      className="no-print sticky top-0 z-[60] flex flex-wrap items-center gap-x-3 gap-y-1.5 bg-[#B4232C] px-5 py-2 text-[13px] font-medium text-white"
    >
      <Headset size={17} weight="fill" className="flex-none" />
      <span className="min-w-0 flex-1">
        {t('panel:impersonation.banner', {
          user: me.user.name,
          tenant: me.tenant.name,
          operator: me.impersonation.impersonatorName,
        })}
        <span className="ml-2 opacity-80" data-testid="impersonation-remaining">
          · {t('panel:impersonation.remaining', { count: left })}
        </span>
      </span>
      <button
        type="button"
        onClick={() => void end()}
        disabled={busy}
        data-testid="impersonation-end"
        className="min-h-8 rounded-md border border-white/60 bg-white/10 px-3 text-[13px] font-semibold text-white hover:bg-white/20 disabled:opacity-60"
      >
        {t('panel:impersonation.end')}
      </button>
    </div>
  );
}
